import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import OpenSeadragon from "openseadragon";
import { ChevronLeft, ChevronRight, Info, Maximize, Minus, Plus, Scan, X, Frame, Share2 } from "lucide-react";
import { Link } from "react-router-dom";
import { media } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Watermark } from "./Protect";

const Ctx = createContext({ open: () => {} });
export const useViewer = () => useContext(Ctx);

const Btn = ({ onClick, label, children, testid, active }) => (
  <button onClick={onClick} aria-label={label} title={label} data-testid={testid}
    className={`flex h-11 w-11 items-center justify-center border transition-colors ${active ? "border-white bg-white text-black" : "border-white/15 bg-black/50 text-white backdrop-blur-md hover:border-white/60"}`}>
    {children}
  </button>
);

const Meta = ({ k, v }) => (v ? (<div className="border-t border-white/10 py-3"><div className="kicker !text-[10px] !text-white/50">{k}</div><div className="mt-1 font-mono text-sm text-white">{v}</div></div>) : null);

const Viewer = ({ list, index, setIndex, onClose }) => {
  const { t, L } = useLang();
  const host = useRef(null);
  const osd = useRef(null);
  const wrap = useRef(null);
  const [ready, setReady] = useState(false);
  const [zoomPct, setZoomPct] = useState(0);
  const [info, setInfo] = useState(false);
  const p = list[index];
  const d = p?.variants?.display;
  const o = p?.original;

  useEffect(() => {
    const v = OpenSeadragon({
      element: host.current, showNavigationControl: false, maxZoomPixelRatio: 2.5, minZoomImageRatio: 0.9,
      visibilityRatio: 0.6, animationTime: 0.9, springStiffness: 7, zoomPerScroll: 1.35, zoomPerClick: 2,
      gestureSettingsMouse: { clickToZoom: false, dblClickToZoom: true }, gestureSettingsTouch: { pinchToZoom: true, flickEnabled: true },
      imageSmoothingEnabled: true, preserveImageSizeOnResize: true,
    });
    const upd = () => {
      const item = v.world.getItemAt(0);
      if (item) setZoomPct(Math.round(item.viewportToImageZoom(v.viewport.getZoom(true)) * 100));
    };
    v.addHandler("animation", upd);
    v.addHandler("open", upd);
    v.addHandler("tile-drawn", () => setReady(true));
    osd.current = v;
    window.__lenis?.stop();
    return () => { v.destroy(); osd.current = null; window.__lenis?.start(); };
  }, []);

  useEffect(() => {
    setReady(false);
    if (osd.current && p?.urls?.display) osd.current.open({ type: "image", url: media(p.urls.display), buildPyramid: true });
  }, [p]);

  const go = useCallback((dir) => setIndex((i) => (i + dir + list.length) % list.length), [list.length, setIndex]);
  const zoom = (f) => { const v = osd.current; if (v) { v.viewport.zoomBy(f); v.viewport.applyConstraints(); } };
  const native = () => { const v = osd.current; const item = v?.world.getItemAt(0); if (item) v.viewport.zoomTo(item.imageToViewportZoom(1)); };
  const fit = () => osd.current?.viewport.goHome();
  const full = () => { if (document.fullscreenElement) document.exitFullscreen(); else wrap.current?.requestFullscreen?.(); };

  useEffect(() => {
    const k = (e) => {
      if (e.key === "Escape" && !document.fullscreenElement) onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "+" || e.key === "=") zoom(1.4);
      if (e.key === "-") zoom(1 / 1.4);
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [go, onClose]);

  return (
    <motion.div ref={wrap} data-testid="photo-viewer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
      className="fixed inset-0 z-[100] bg-[#060606]" data-lenis-prevent>
      {!ready && p?.urls?.medium && (
        <img src={media(p.urls.medium)} alt="" className="pointer-events-none absolute inset-0 m-auto max-h-full max-w-full object-contain p-4 opacity-80" />
      )}
      <div ref={host} className="osd-host absolute inset-0" data-testid="photo-viewer-canvas" />
      <Watermark className="absolute bottom-24 left-1/2 -translate-x-1/2 md:bottom-28" />
      <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between bg-gradient-to-b from-black/70 to-transparent p-4 md:p-6">
        <div className="pointer-events-auto max-w-[60%]">
          <div className="kicker !text-white/60">{String(index + 1).padStart(2, "0")} / {String(list.length).padStart(2, "0")} · {t(p?.category)}</div>
          <div className="font-display mt-1 truncate text-xl font-bold text-white md:text-2xl" data-testid="photo-viewer-title">{L(p, "title")}</div>
        </div>
        <div className="pointer-events-auto flex gap-2">
          <Link to={`/photo/${p?.slug}`} onClick={onClose} aria-label={t("photoPage")} title={t("photoPage")} data-testid="viewer-photo-page-link"
            className="flex h-11 w-11 items-center justify-center border border-white/15 bg-black/50 text-white backdrop-blur-md hover:border-white/60"><Share2 size={16} /></Link>
          <Btn onClick={() => setInfo((s) => !s)} label={t("info")} testid="viewer-info-button" active={info}><Info size={17} /></Btn>
          <Btn onClick={full} label="Fullscreen" testid="viewer-fullscreen-button"><Maximize size={17} /></Btn>
          <Btn onClick={onClose} label={t("close")} testid="viewer-close-button"><X size={18} /></Btn>
        </div>
      </div>
      {list.length > 1 && (<>
        <button data-testid="viewer-prev-button" onClick={() => go(-1)} aria-label="Previous" className="absolute left-2 top-1/2 flex h-14 w-14 -translate-y-1/2 items-center justify-center text-white/70 transition-colors hover:text-white md:left-5"><ChevronLeft size={30} /></button>
        <button data-testid="viewer-next-button" onClick={() => go(1)} aria-label="Next" className="absolute right-2 top-1/2 flex h-14 w-14 -translate-y-1/2 items-center justify-center text-white/70 transition-colors hover:text-white md:right-5"><ChevronRight size={30} /></button>
      </>)}
      <div className="absolute inset-x-0 bottom-0 flex flex-wrap items-end justify-between gap-3 bg-gradient-to-t from-black/80 to-transparent p-4 md:p-6">
        <div className="font-mono text-[11px] leading-relaxed tracking-wider text-white/75" data-testid="viewer-dimensions">
          {d && <div>{t("displayRes")}: {d.width} × {d.height} px</div>}
          {o?.width && <div className="text-white/55">{t("originalRes")}: {o.width} × {o.height} px{o.megapixels ? ` · ${o.megapixels} MP` : ""}</div>}
        </div>
        <div className="flex items-center gap-2">
          <span className="mr-2 w-14 text-right font-mono text-xs text-white" data-testid="viewer-zoom-level">{zoomPct}%</span>
          <Btn onClick={() => zoom(1 / 1.5)} label={t("zoomOut")} testid="viewer-zoom-out-button"><Minus size={17} /></Btn>
          <Btn onClick={() => zoom(1.5)} label={t("zoomIn")} testid="viewer-zoom-in-button"><Plus size={17} /></Btn>
          <Btn onClick={fit} label={t("fit")} testid="viewer-fit-button"><Scan size={17} /></Btn>
          <button onClick={native} data-testid="viewer-native-button" className="h-11 border border-white/15 bg-black/50 px-4 font-mono text-[11px] tracking-[0.2em] text-white backdrop-blur-md hover:border-white/60">1:1</button>
        </div>
      </div>
      <AnimatePresence>
        {info && (
          <motion.aside data-testid="viewer-info-panel" initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ duration: 0.5, ease: [0.7, 0, 0.2, 1] }}
            className="absolute bottom-0 right-0 top-0 w-full max-w-sm overflow-y-auto border-l border-white/10 bg-[#0b0b0b]/95 p-6 pt-24 backdrop-blur-xl">
            <h3 className="font-display text-2xl font-bold text-white">{L(p, "title")}</h3>
            {L(p, "description") && <p className="mt-3 text-sm leading-relaxed text-white/70">{L(p, "description")}</p>}
            <div className="mt-6">
              <Meta k={t("location")} v={p?.location} />
              <Meta k={t("camera")} v={p?.camera || p?.exif?.camera} />
              <Meta k={t("lens")} v={p?.lens || p?.exif?.lens} />
              <Meta k={t("settings")} v={p?.settings || [p?.exif?.focal, p?.exif?.aperture, p?.exif?.shutter, p?.exif?.iso].filter(Boolean).join(" · ")} />
              <Meta k={t("displayRes")} v={d && `${d.width} × ${d.height} px · WebP`} />
              <Meta k={t("originalRes")} v={o?.width && `${o.width} × ${o.height} px · ${o.format}`} />
              <Meta k="©" v={p?.copyright} />
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to={`/frame-visualizer?photo=${p?.slug}`} onClick={onClose} data-testid="viewer-visualize-link" className="btn-line"><Frame size={14} /> {t("visualize")}</Link>
              <Link to={`/photo/${p?.slug}`} onClick={onClose} data-testid="viewer-photo-page-button" className="btn-line"><Share2 size={14} /> {t("photoPage")}</Link>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const ViewerProvider = ({ children }) => {
  const [state, setState] = useState(null);
  const open = useCallback((list, index = 0) => setState({ list, index }), []);
  const setIndex = useCallback((fn) => setState((s) => ({ ...s, index: typeof fn === "function" ? fn(s.index) : fn })), []);
  return (
    <Ctx.Provider value={{ open }}>
      {children}
      <AnimatePresence>
        {state && <Viewer key="viewer" list={state.list} index={state.index} setIndex={setIndex} onClose={() => setState(null)} />}
      </AnimatePresence>
    </Ctx.Provider>
  );
};
